<template>
  <TirPmDropdown
    :variant="TirPmDropdownVariantEnum.Detached"
    class="task-status-dropdown"
    label="Статус"
    placeholder="Выберите статус"
    :options="dropdownOptions"
    :selected-option="selectedOption"
    :size="TirPmDropdownSizeEnum.Light"
    width="fit-content"
    :is-disabled-popover="(options.length <= 1 && !!modelValue) || !editing"
    popover-width="auto"
    option-list-scroll-height="11rem"
  >
    <template v-if="editing" #optionList="{ option }">
      <div class="task-status-dropdown__option" @click="emit('update:modelValue', option.Id)">
        <div class="task-status-dropdown__status">
          <div class="task-status-dropdown__status-bg" :style="{ backgroundColor: option.Color }" />
          <div
            class="task-status-dropdown__status-dot"
            :style="{ backgroundColor: option.Color }"
          />
          <span class="task-status-dropdown__status-text" :style="{ color: option.Color }">{{
            option.Name
          }}</span>
        </div>

        <TirPmTooltipElement v-if="option.Description">
          <template #content>
            <div class="task-status-dropdown__option__description">
              {{ option.Description }}
            </div>
          </template>
          <template #default>
            <InformationCircleIcon class="task-status-dropdown__option__icon" />
          </template>
        </TirPmTooltipElement>
      </div>
    </template>
    <template #selectedContent>
      <div class="task-status-dropdown__selected" v-if="selectedOption">
        <TirPmTooltipElement :isDisabled="!selectedOption.Description">
          <div class="task-status-dropdown__status">
            <div
              class="task-status-dropdown__status-bg"
              :style="{ backgroundColor: selectedOption.Color }"
            />
            <div
              class="task-status-dropdown__status-dot"
              :style="{ backgroundColor: selectedOption.Color }"
            />
            <span
              class="task-status-dropdown__status-text"
              :style="{ color: selectedOption.Color }"
              >{{ selectedOption.Name }}</span
            >
          </div>
          <template #content>
            <div class="task-status-dropdown__status-description">
              {{ selectedOption.Description }}
            </div>
          </template>
        </TirPmTooltipElement>
      </div>
    </template>
  </TirPmDropdown>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue';
import { TirPmDropdown, TirPmDropdownSizeEnum, TirPmTooltipElement } from 'tir-components';
import { StatusModel } from '../../models';
import { StatusDropdownOptionModel } from './models';
import { InformationCircleIcon } from 'tir-style-system/icons/outline';
import { TirPmDropdownVariantEnum } from 'tir-pm-dropdown/src/enums';

const emit = defineEmits<{
  /**Событие обновления выбранного статуса */
  (e: 'update:modelValue', value: number): void;
}>();

const props = defineProps({
  /**Возможность редактировать */
  editing: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /**Выбранный статус */
  modelValue: {
    type: Number,
  },
  /**Статусы для выбора */
  options: {
    type: Array as PropType<Array<StatusModel>>,
    default: [],
  },
});

/**Опции дропдауна */
const dropdownOptions = computed(() =>
  props.options.map(
    (option) =>
      new StatusDropdownOptionModel({
        Id: option.Id,
        Name: option.Name,
        Color: option.Color,
        Description: option.Description,
      }),
  ),
);

/**Выбранная опция */
const selectedOption = computed(() =>
  props.modelValue !== undefined
    ? dropdownOptions.value.find((option) => props.modelValue === option.Id)
    : undefined,
);
</script>

<style lang="scss" scoped>
.task-status-dropdown {
  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem;
    &__icon {
      width: 1.5rem;
      height: 1.5rem;
    }
    &__description {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 2.25rem;
      padding: 0 0.5rem;
      word-break: break-word;
    }
  }
  &__status {
    position: relative;
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    overflow: hidden;

    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }
  &__status-dot {
    position: relative;
    z-index: 2;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
  }
  &__status-text {
    position: relative;
    z-index: 2;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    text-transform: uppercase;
  }
  &__status-description {
    font-size: 0.75rem;
    padding: 0.75rem;
    overflow-wrap: break-word;
  }
  &__status-bg {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    opacity: 0.1;
    z-index: 1;
  }
  &__area {
    position: relative;
    overflow: hidden;
    display: flex;
    border-radius: var(--6);
    border: 0.063rem solid var(--neutral-80, #52575f);
    align-items: start;
    min-width: auto;
    cursor: pointer;
    box-sizing: border-box;
    width: fit-content;
    background-color: var(--neutral-0, #ffffff);
    transition: border 0.2s ease;
    padding-right: 0.625rem;
    outline: none;

    .task-status-dropdown__placeholder {
      font-size: 0.875rem;
    }
    &.is-selected {
      border-color: transparent;
    }
  }

  &__desc-select {
    flex-grow: 1;
    min-height: 2rem;
    padding-right: 0.75rem;
    padding-left: 0.75rem;
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
    display: flex;
    justify-content: center;
    flex-direction: column;
    min-width: 0;

    &.is-selected {
      padding-left: 1.25rem;
    }
  }

  &__selected-item {
    font-size: 1rem;
    color: var(--text-tertiary);
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
    user-select: none;
    height: 0;
    transition: height 0.2s ease, opacity 0.2s ease;
    overflow: hidden;
    opacity: 0;
  }
  &__selected-item--show {
    height: fit-content;
    opacity: 1;
  }

  &__icon-open {
    display: flex;
    padding-top: 0.5rem;
    svg {
      margin: auto;
      width: 1rem;
      height: 1rem;
      transition: transform 0.2s ease;
      color: var(--neutral-90);
    }

    &.is-opened {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  &__label {
    color: var(--text-secondary);
    user-select: none;
    transition: font-size 0.2s ease;
    font-size: 0.75rem;
    line-height: 1rem;
  }

  &__target {
    display: flex;
    flex-direction: column;
    row-gap: 0.25rem;
  }
  &__target-bg {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    opacity: 0.1;
  }

  &__selected-content {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
  &__selected-content-text {
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  &__selected-content-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
  }
}
</style>
